import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import * as React from 'react';
import { passwordSchema } from '@/api/auth/auth.schema';
import { Field } from '@/components/ui/admin/field';
import { LoginBrandPanel, LoginBrandStrip } from '@/components/ui/admin/login';
import { PasswordField } from '@/components/ui/admin/profile';
import { Icon } from '@/components/ui/icon';
import { useToast } from '@/hooks/use-toast';
import { useForgotPassword, useResetPassword } from '@/services/auth';

export const Route = createFileRoute('/_auth/forgot-password')({
  beforeLoad: ({ context: { auth } }) => {
    if (auth.isAuthenticated) {
      return redirect({ to: '/' });
    }
  },
  component: RouteComponent,
});

/**
 * Recuperação de senha em duas etapas, mesmo layout do login: (1) e-mail → o backend manda
 * um código de 6 dígitos; (2) código + nova senha. A resposta da etapa 1 é sempre de sucesso,
 * exista a conta ou não.
 */
function RouteComponent() {
  const toast = useToast();
  const [step, setStep] = React.useState<'email' | 'code'>('email');
  const [email, setEmail] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [localError, setLocalError] = React.useState('');

  const forgot = useForgotPassword();
  const reset = useResetPassword();
  const error = localError || forgot.error?.message || reset.error?.message;

  const clearErrors = () => {
    setLocalError('');
    if (forgot.error) forgot.reset();
    if (reset.error) reset.reset();
  };

  const sendCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return setLocalError('Informe o e-mail da sua conta.');
    setLocalError('');
    forgot.mutate({ email: email.trim() }, { onSuccess: () => setStep('code') });
  };

  const resend = () =>
    forgot.mutate(
      { email: email.trim() },
      {
        onSuccess: () =>
          toast.success('Se o e-mail estiver cadastrado, enviamos um novo código.'),
      },
    );

  const submitReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) return setLocalError('O código tem 6 dígitos.');

    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) return setLocalError(parsed.error.issues[0]?.message ?? 'Senha inválida.');
    if (password !== confirm) return setLocalError('As senhas não conferem.');

    setLocalError('');
    reset.mutate(
      { email: email.trim(), otp, newPassword: password },
      {
        onSuccess: () => {
          toast.success('Senha redefinida! Entre com a nova senha.');
          window.location.assign('/sign-in');
        },
      },
    );
  };

  const busy = forgot.isPending || reset.isPending || reset.isSuccess;

  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground lg:flex-row">
      <LoginBrandPanel />
      <LoginBrandStrip />

      <section className="flex flex-1 basis-[400px] items-center justify-center bg-sunken px-5 pb-10 pt-2 lg:basis-0 lg:py-12">
        <form
          onSubmit={step === 'email' ? sendCode : submitReset}
          noValidate
          className="flex w-full max-w-[400px] flex-col gap-7"
        >
          <div className="flex flex-col gap-2">
            <h2 className="font-display m-0 text-[clamp(26px,3vw,30px)] font-extrabold tracking-[-.02em] text-white">
              {step === 'email' ? 'Esqueci a senha' : 'Nova senha'}
            </h2>
            <p className="m-0 text-sm text-muted-foreground">
              {step === 'email'
                ? 'Informe o e-mail da sua conta e enviaremos um código de 6 dígitos.'
                : `Se ${email.trim()} estiver cadastrado, o código chega em instantes. Ele vale por 10 minutos.`}
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-[14px] border border-destructive/40 bg-destructive/10 px-3.5 py-3 text-[13px] leading-snug text-destructive"
            >
              <Icon name="circle-alert" className="mt-px size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col gap-4">
            {step === 'email' ? (
              <Field
                label="E-mail"
                type="email"
                autoComplete="username"
                placeholder="voce@chordfy.alistese.com.br"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearErrors();
                }}
              />
            ) : (
              <>
                <Field
                  label="Código"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, ''));
                    clearErrors();
                  }}
                />
                <PasswordField
                  label="Nova senha"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearErrors();
                  }}
                />
                <PasswordField
                  label="Confirmar nova senha"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    clearErrors();
                  }}
                />
                <p className="m-0 px-1 text-xs text-faint">
                  Mínimo de 8 caracteres, com uma letra minúscula e um número.
                </p>
              </>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="flex h-12 items-center justify-center gap-2 rounded-full bg-sand text-sm font-bold text-on-sand transition-[background-color,transform] duration-150 hover:bg-sand/90 active:scale-[.98] disabled:opacity-70"
          >
            {busy ? (
              <>
                <span className="size-4 animate-spin rounded-full border-2 border-on-sand/25 border-t-on-sand" />
                {step === 'email' ? 'Enviando…' : 'Salvando…'}
              </>
            ) : (
              <>
                {step === 'email' ? 'Enviar código' : 'Redefinir senha'}
                <Icon name="arrow-right" className="size-4" />
              </>
            )}
          </button>

          <div className="flex items-center justify-between gap-3 border-t border-border pt-5">
            <Link
              to="/sign-in"
              className="text-[13px] font-semibold text-sand hover:text-sand/80"
            >
              Voltar ao login
            </Link>
            {step === 'code' && (
              <button
                type="button"
                onClick={resend}
                disabled={busy}
                className="text-[13px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-60"
              >
                Reenviar código
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
